import * as React from 'react';
import { SingleValueComponent } from '../common/types';
import { Link as RouterLink } from 'react-router-dom';
import { Link as ExternalLink } from 'ui/link';
import styles from './link-heading.scss';
import { LinkContextConsumer } from 'app/components/contexts/link-context';
import { Expandable, ShouldTruncateTextAndLines, truncateStringToMaxLines } from 'ui/expandable';
import { ReactNode, useCallback } from 'react';

function useContentRender(value: string, visibleTextLength: number, maxLines: number) {
  return useCallback((collapsed: boolean) => {
    const newValue = value || '';

    return collapsed ? truncateStringToMaxLines(value, maxLines, visibleTextLength) : newValue;
  },
    [value, visibleTextLength]
  );
}

export interface PrimaryTitleProps extends SingleValueComponent<string> {
  url?: any;
  children?: ReactNode;
  expandAll?: boolean;
  textDisplayMore: string;
  textDisplayLess: string;
  maxLines?: number;
  visibleTextLength?: number;
}

export const LinkHeading: React.FunctionComponent<PrimaryTitleProps> = (props: PrimaryTitleProps) => {
  const { className, value, url, expandAll, textDisplayLess, textDisplayMore, maxLines, visibleTextLength } = props;
  const classes = props.url ? styles.link : '';
  const shouldTruncate = ShouldTruncateTextAndLines(value, visibleTextLength, maxLines) && !expandAll;
  const contentRender = useContentRender(value, visibleTextLength, maxLines);
  const valueComponent = shouldTruncate ?
    <Expandable content={contentRender} textDisplayLess={textDisplayLess} textDisplayMore={textDisplayMore} />
    :
    value;
  const printOnly = shouldTruncate ?
    <h1 className={`${styles.printOnly} link-heading-print-only`}>{value}</h1>
    :
    null;

  return (
    <LinkContextConsumer>
      {parentLink => parentLink || !url ?
        <>
          <h1 id={props.componentId} className={`${styles.primaryTitle} ${className}`}>
            {valueComponent}
          </h1>
          {printOnly}
        </>
        :
        <RouterLink to={url} className={classes}>
          <>
            {printOnly}
            <h1 id={props.componentId} className={`${styles.primaryTitle} ${className}`}>
              {valueComponent}
            </h1>
          </>
        </RouterLink>
      }
    </LinkContextConsumer>);
};

export const ExternalLinkHeading: React.FunctionComponent<PrimaryTitleProps> = (props: PrimaryTitleProps) => {
  const { className, value, url, expandAll, textDisplayLess, textDisplayMore, maxLines, visibleTextLength } = props;
  const inlineStyle = props.url ? styles.link : '';
  const shouldTruncate = ShouldTruncateTextAndLines(value, visibleTextLength, maxLines) && !expandAll;
  const contentRender = useContentRender(value, visibleTextLength, maxLines);
  const printOnly = shouldTruncate ?
    <h1 className={`${styles.printOnly} link-heading-print-only`}>{value}</h1>
    :
    null;

  return (
    <ExternalLink url={url} className={inlineStyle}>
      <>
        {printOnly}
        <h1 id={props.componentId} className={`${styles.primaryTitle} ${className}`}>
          {shouldTruncate ?
            <Expandable content={contentRender} textDisplayLess={textDisplayLess} textDisplayMore={textDisplayMore} />
            :
            value
          }
        </h1>
      </>
    </ExternalLink>);
};
